import Phaser from 'phaser';
import { Soldier } from '../entities/Soldier';
import { Enemy, EnemyType, ENEMY_SIGHT_RADIUS, ENEMY_HEARING_RADIUS } from '../entities/Enemy';
import { Grenade } from '../entities/Grenade';
import { Rocket } from '../entities/Rocket';
import { ExplosiveBarrel, BARREL_SPLASH_RADIUS, BARREL_SPLASH_DAMAGE } from '../entities/ExplosiveBarrel';
import { MovementSystem } from '../systems/MovementSystem';
import { CombatSystem } from '../systems/CombatSystem';
import { ExplosionSystem } from '../systems/ExplosionSystem';
import { SquadManager } from '../systems/SquadManager';
import { EnemyManager } from '../systems/EnemyManager';
import { MissionSystem } from '../systems/MissionSystem';
import { AudioSystem } from '../systems/AudioSystem';
import { CampaignState, ProgressStore } from '../state/CampaignState';
import { TerrainGenerator, GeneratedMap } from '../generation/TerrainGenerator';
import { LevelFactory } from '../generation/LevelFactory';
import { MissionOverlay, MissionLayout } from '../generation/MissionOverlay';
import { getTilesetConfig } from './PreloadScene';
import { isTileWalkable } from '../generation/TilesetLoader';
import { CollisionGeometryBuilder } from '../physics/CollisionGeometryBuilder';
import { buildPolysFromGridMap } from 'navmesh';
import { AmmoPickup, AmmoType } from '../entities/AmmoPickup';
import { DestructibleBuilding } from '../entities/DestructibleBuilding';

const TILE_SIZE = 32;

// Friendly fire — when true, player bullets can damage friendly soldiers
const FRIENDLY_FIRE = true;

// Grenade config
const GRENADE_RADIUS = 64;
const GRENADE_DAMAGE = 50;
const GRENADE_COOLDOWN = 1000;
// Maximum distance (in pixels) a grenade can be thrown from the throwing soldier
const GRENADE_MAX_THROW_RADIUS = 256;

// Rocket config
const ROCKET_RADIUS = 96;
const ROCKET_DAMAGE = 75;
const ROCKET_TROOPER_EXPLOSION_RADIUS = 64;
const ROCKET_TROOPER_EXPLOSION_DAMAGE = 40;
const ROCKET_COOLDOWN = 2000;
// Proximity radius at which a rocket detonates on contact with a target
const ROCKET_TRIGGER_RADIUS = 14;

// Destroy-target building tile location (tile coords)
const DESTROY_TARGET_TILE_X = 18;
const DESTROY_TARGET_TILE_Y = 5;

// Squad spawns
const SQUAD_SPAWNS = [
  { x: 4, y: 4 },
  { x: 5, y: 4 },
  { x: 4, y: 5 },
  { x: 5, y: 5 },
];

// Enemy spawns: first 3 riflemen, last 1 rocket trooper
const ENEMY_SPAWNS: { x: number; y: number; type: EnemyType }[] = [
  { x: 12, y: 3,  type: EnemyType.RIFLEMAN },
  { x: 15, y: 8,  type: EnemyType.RIFLEMAN },
  { x: 17, y: 12, type: EnemyType.RIFLEMAN },
  { x: 10, y: 6,  type: EnemyType.ROCKET_TROOPER },
];

export class GameScene extends Phaser.Scene {
  /** Set to true to use a procedurally generated level instead of level1.json. */
  static useGeneratedLevel: boolean = false;
  /** The most recently generated map (used for spawn placement). */
  static generatedMap: GeneratedMap | null = null;
  /** The most recently generated mission layout. */
  static missionLayout: MissionLayout | null = null;

  /**
   * Generate a new procedural level and cache it so GameScene.create() can use it.
   * Call this before starting/restarting GameScene.
   */
  static generateNewLevel(game: Phaser.Game, seed?: number): GeneratedMap {
    const generator = new TerrainGenerator();
    const tilesetConfig = getTilesetConfig();
    if (tilesetConfig) {
      generator.setTilesetConfig(tilesetConfig);
    }
    const map = generator.generate(40, 40, seed);
    GameScene.generatedMap = map;

    const factory = new LevelFactory();
    const json = factory.buildTiledJSON(map);

    // Remove stale cached map and re-add with the correct Phaser cache format.
    // Phaser's TilemapCache.add() expects: (key, { data: rawObject, format: number })
    game.cache.tilemap.remove('level_generated');
    game.cache.tilemap.add('level_generated', {
      data: json,
      format: Phaser.Tilemaps.Formats.TILED_JSON,
    });

    GameScene.useGeneratedLevel = true;

    // Generate mission layout for this map
    const overlay = new MissionOverlay();
    GameScene.missionLayout = overlay.generate(map, CampaignState.missionNumber, seed);

    return map;
  }

  private soldier!: Soldier;
  private squadManager!: SquadManager;
  private enemyManager!: EnemyManager;
  private missionSystem!: MissionSystem;
  private grenades: Grenade[] = [];
  private rockets: Rocket[] = [];
  private ammoPickups: AmmoPickup[] = [];
  private barrels: ExplosiveBarrel[] = [];
  private buildings: DestructibleBuilding[] = [];
  private map!: Phaser.Tilemaps.Tilemap;
  private waterAnimGraphics: Phaser.GameObjects.Graphics[] = [];
  private transitioning: boolean = false;
  private movementSystem!: MovementSystem;
  private combatSystem!: CombatSystem;
  private explosionSystem!: ExplosionSystem;
  private groundLayer!: Phaser.Tilemaps.TilemapLayer;
  private mapWidth: number = 0;
  private mapHeight: number = 0;
  private audioSystem!: AudioSystem;
  private killCount: number = 0;

  // Time of day
  private timeOfDay: 'day' | 'night' = 'day';

  // Walkability debug overlay
  private walkabilityOverlay!: Phaser.GameObjects.Graphics;
  private walkabilityOverlayVisible: boolean = false;

  // Enemy detection debug overlay
  private debugOverlay!: Phaser.GameObjects.Graphics;
  private debugOverlayVisible: boolean = false;

  /** Timestamp (ms) when this scene's create() ran. Used to discard pointer
   *  events whose downTime predates scene start (e.g. click-through from the
   *  BriefingScene "START MISSION" button). */
  private sceneStartTime: number = 0;

  // Collision geometry debug overlay (F2)
  private collisionGeometryOverlay!: Phaser.GameObjects.Graphics;
  private collisionGeometryOverlayVisible: boolean = false;
  private collisionGeometryLabel: Phaser.GameObjects.Text | null = null;

  // Grenade state (UI modes only — ammo/cooldown now per-soldier)
  private grenadeTargetingMode: boolean = false;
  private suppressNextLeftUp: boolean = false;
  private grenadeHudText!: Phaser.GameObjects.Text;
  private targetingCursor!: Phaser.GameObjects.Graphics;

  // Rocket state (UI mode only — ammo/cooldown now per-soldier)
  private rocketMode: boolean = false;
  private rocketHudText!: Phaser.GameObjects.Text;
  private rocketModeIndicator!: Phaser.GameObjects.Text;

  // Double-tap shoot detection (touch devices)
  private lastTapTime: number = 0;
  private lastTapX: number = 0;
  private lastTapY: number = 0;

  // Bound event handler — stored so it can be removed on shutdown
  private readonly onContextMenu = (e: Event) => e.preventDefault();

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    // Record scene start time so we can discard pointer events that leaked
    // from the previous scene (e.g. the "START MISSION" button click).
    this.sceneStartTime = this.time.now;

    // Reset all instance state that persists across scene restarts.
    // Phaser reuses the scene object so these fields retain their previous values
    // unless explicitly cleared here.
    this.transitioning = false;
    this.grenadeTargetingMode = false;
    this.rocketMode = false;
    this.suppressNextLeftUp = false;
    this.lastTapTime = 0;
    this.barrels = [];
    this.grenades = [];
    this.rockets = [];
    this.waterAnimGraphics = [];
    this.debugOverlayVisible = false;
    this.collisionGeometryOverlayVisible = false;
    this.collisionGeometryLabel = null;

    // Prevent this.events.on() handlers from accumulating across restarts.
    // Phaser does not clear scene EventEmitter listeners on scene shutdown.
    this.events.off('buildingDestroyed');
    this.events.off('enemyRocketExplode');
    this.events.off('muzzleFlash');
    this.events.off('bloodSplat');
    this.events.off('soldierDied');
    this.events.off('enemyDied');
    this.events.off('enemyRocketLaunch');
    this.events.off('touchCommand');

    const staticLevelKey = `level${Math.min(CampaignState.missionNumber, 4)}`;
    const tilemapKey = GameScene.useGeneratedLevel ? 'level_generated' : staticLevelKey;

    // For generated levels, verify the cache entry exists before making the tilemap
    if (GameScene.useGeneratedLevel && !this.cache.tilemap.has(tilemapKey)) {
      console.error('Generated tilemap not found in cache — falling back to level1');
      GameScene.useGeneratedLevel = false;
    }

    const finalKey = GameScene.useGeneratedLevel ? 'level_generated' : staticLevelKey;

    // Read level metadata from the tilemap JSON (only for static levels)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const levelMeta: any = !GameScene.useGeneratedLevel
      ? (this.cache.tilemap.get(staticLevelKey) as any)?.data?.metadata ?? null
      : null;
    this.timeOfDay = levelMeta?.timeOfDay ?? 'day';

    const terrain: string = levelMeta?.terrain ?? 'default';
    window.dispatchEvent(new CustomEvent('warboys:terrain-changed', { detail: { terrain } }));

    this.map = this.make.tilemap({ key: finalKey });
    const map = this.map;
    const tileset = map.addTilesetImage('tiles', 'tiles');
    if (!tileset) { console.error('Failed to load tileset'); return; }

    const groundLayer = map.createLayer('ground', tileset, 0, 0);
    if (!groundLayer) { console.error('Failed to create ground layer'); return; }
    this.groundLayer = groundLayer;

    // Create water overlay - renders above soldiers to simulate wading
    const WATER_TILE_INDICES = [11]; // water tile index from tileset.json (id: "water", index: 11)
    const waterOverlay = this.add.graphics().setDepth(11); // above soldiers (depth 10)
    for (let row = 0; row < map.height; row++) {
      for (let col = 0; col < map.width; col++) {
        const tile = groundLayer.getTileAt(col, row);
        if (tile && WATER_TILE_INDICES.includes(tile.index)) {
          // Cover lower half of tile to create "waist deep" wading effect
          waterOverlay.fillStyle(0x1a6b8a, 0.55);
          waterOverlay.fillRect(col * TILE_SIZE, row * TILE_SIZE + TILE_SIZE / 2, TILE_SIZE, TILE_SIZE / 2);
        }
      }
    }

    // Animated water tiles — prototype colour-cycle (depth 9, beneath waterOverlay at 11)
    // PROTOTYPE: no sprite sheet exists yet; colour-cycling between two water shades as placeholder.
    const waterTiles = groundLayer.filterTiles((t: Phaser.Tilemaps.Tile) => t.index === 11);
    for (const tile of waterTiles) {
      const gfx = this.add.graphics().setDepth(9);
      const tileX = tile.pixelX;
      const tileY = tile.pixelY;
      // Draw initial fill so something is visible before the first tween update
      gfx.fillStyle(0x1a6b8a, 1);
      gfx.fillRect(tileX, tileY, TILE_SIZE, TILE_SIZE);
      this.waterAnimGraphics.push(gfx);

      const proxy = { t: Math.random() };
      const duration = 1600 + Math.random() * 800; // 1600–2400 ms
      this.tweens.add({
        targets: proxy,
        t: 1,
        duration,
        yoyo: true,
        repeat: -1,
        delay: Math.random() * 2000,
        onUpdate: () => {
          // Interpolate between dark (0x1a6b8a) and lighter (0x2a8aaa)
          const r = Math.round(0x1a + (0x2a - 0x1a) * proxy.t);
          const g = Math.round(0x6b + (0x8a - 0x6b) * proxy.t);
          const b = Math.round(0x8a + (0xaa - 0x8a) * proxy.t);
          const colour = (r << 16) | (g << 8) | b;
          gfx.clear();
          gfx.fillStyle(colour, 1);
          gfx.fillRect(tileX, tileY, TILE_SIZE, TILE_SIZE);
        },
      });
    }

    // Night darkness overlay — depth 5 (above tilemap at 0, below soldiers at 10)
    if (this.timeOfDay === 'night') {
      const darknessW = Math.max(map.widthInPixels, 2000);
      const darknessH = Math.max(map.heightInPixels, 2000);
      this.add.rectangle(darknessW / 2, darknessH / 2, darknessW, darknessH, 0x000022, 0.65)
        .setDepth(5);
    }

    // Set collision based on walkability metadata from tileset.json
    const tilesetConfig = getTilesetConfig();
    if (tilesetConfig) {
      // Collect all non-walkable tile indices and set collision on them
      const nonWalkableIndices = tilesetConfig.tiles
        .filter((t) => !t.walkable)
        .map((t) => t.index);
      if (nonWalkableIndices.length > 0) {
        groundLayer.setCollision(nonWalkableIndices);
      } else {
        // Fallback: hard-coded indices
        groundLayer.setCollisionBetween(2, 2);
        groundLayer.setCollisionBetween(4, 4);
      }
    } else {
      // Fallback when tileset.json wasn't loaded
      groundLayer.setCollisionBetween(2, 2); // wall
      groundLayer.setCollisionBetween(4, 4); // tree
    }

    this.mapWidth = map.widthInPixels;
    this.mapHeight = map.heightInPixels;
    this.cameras.main.setBounds(0, 0, this.mapWidth, this.mapHeight);

    const grid = this.buildGrid(map);
    this.movementSystem = new MovementSystem(grid, TILE_SIZE);
    this.movementSystem.buildNavmesh(grid);

    // Build Matter static bodies for blocked regions so soldiers physically
    // cannot walk through walls.  We reuse buildPolysFromGridMap from the
    // navmesh package to merge adjacent blocked tiles into rectangular convex
    // hulls (same technique as the navmesh, but with the predicate inverted).
    // Each hull becomes a single static Matter rectangle (collision category 2).
    const WALL_CATEGORY = 2;
    const blockedPolys: { x: number; y: number }[][] = buildPolysFromGridMap(
      grid,
      TILE_SIZE,
      TILE_SIZE,
      (value: number) => value === 1,  // 1 = blocked tile
    ) as { x: number; y: number }[][];
    for (const poly of blockedPolys) {
      const xs = (poly as { x: number; y: number }[]).map((p) => p.x);
      const ys = (poly as { x: number; y: number }[]).map((p) => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      this.matter.add.rectangle(
        (minX + maxX) / 2,
        (minY + maxY) / 2,
        maxX - minX,
        maxY - minY,
        {
          isStatic: true,
          friction: 0,
          frictionAir: 0,
          collisionFilter: { category: WALL_CATEGORY, mask: 1 },
        },
      );
    }

    this.combatSystem = new CombatSystem(this, this.mapWidth, this.mapHeight);
    this.combatSystem.setWallChecker((x, y) => {
      const tile = this.groundLayer.getTileAtWorldXY(x, y);
      if (!tile) return false;
      const cfg = getTilesetConfig();
      return cfg ? !isTileWalkable(cfg, tile.index) : tile.index === 2 || tile.index === 4;
    });
    this.explosionSystem = new ExplosionSystem(this);
    this.squadManager = new SquadManager(this);
    this.enemyManager = new EnemyManager();
    this.audioSystem = new AudioSystem(this);
    this.killCount = 0;

    // Persist progress — save current mission number so the player can resume
    // if the browser is closed mid-mission.
    ProgressStore.save(CampaignState.missionNumber);

    // Animations
    if (!this.anims.exists('soldier-idle')) {
      this.anims.create({
        key: 'soldier-idle',
        frames: this.anims.generateFrameNumbers('soldier', { start: 0, end: 2 }),
        frameRate: 8, repeat: -1,
      });
    }
    if (!this.anims.exists('soldier-walk')) {
      this.anims.create({
        key: 'soldier-walk',
        frames: this.anims.generateFrameNumbers('soldier', { start: 0, end: 2 }),
        frameRate: 12, repeat: -1,
      });
    }

    // Determine spawn positions — generated layout > level metadata > hardcoded constants
    const layout = GameScene.useGeneratedLevel ? GameScene.missionLayout : null;
    const squadSpawns = layout
      ? layout.playerSpawn
      : (levelMeta?.startingPositions ?? SQUAD_SPAWNS);
    const enemyTypeMap: Record<string, EnemyType> = {
      RIFLEMAN: EnemyType.RIFLEMAN, rifleman: EnemyType.RIFLEMAN,
      ROCKET_TROOPER: EnemyType.ROCKET_TROOPER, rocket_trooper: EnemyType.ROCKET_TROOPER,
    };
    const enemySpawns: { x: number; y: number; type: EnemyType; patrolPath?: string; patrolLoop?: string; waypoints?: { x: number; y: number }[] }[] = layout
      ? layout.enemySpawns.map((s) => ({ ...s, type: s.type as EnemyType }))
      : (levelMeta?.enemySpawns?.map((s: { x: number; y: number; type: string; patrolPath?: string; patrolLoop?: string }) => ({
          x: s.x, y: s.y, type: enemyTypeMap[s.type] ?? EnemyType.RIFLEMAN,
          patrolPath: s.patrolPath, patrolLoop: s.patrolLoop,
        })) ?? ENEMY_SPAWNS);

    // Squad — spawn from campaign roster (living soldiers only)
    const livingRoster = CampaignState.getLiving();
    const spawnsToUse = Math.min(squadSpawns.length, livingRoster.length);
    for (let i = 0; i < spawnsToUse; i++) {
      const spawn = squadSpawns[i];
      const name = livingRoster[i]?.name ?? '';
      const s = new Soldier(
        this,
        spawn.x * TILE_SIZE + TILE_SIZE / 2,
        spawn.y * TILE_SIZE + TILE_SIZE / 2,
        name
      );
      s.groundLayer = this.groundLayer;
      this.squadManager.addSoldier(s);
      if (i === 0) this.soldier = s;
    }
    // Fallback: if CampaignState has no living soldiers (e.g., first run before init)
    if (spawnsToUse === 0) {
      const spawn = squadSpawns[0];
      this.soldier = new Soldier(
        this,
        spawn.x * TILE_SIZE + TILE_SIZE / 2,
        spawn.y * TILE_SIZE + TILE_SIZE / 2
      );
      this.soldier.groundLayer = this.groundLayer;
      this.squadManager.addSoldier(this.soldier);
    }
    this.squadManager.selectAll();

    // Enemies
    for (const spawn of enemySpawns) {
      const enemy = new Enemy(
        this, spawn.x, spawn.y,
        this.combatSystem, this.movementSystem,
        this.mapWidth, this.mapHeight,
        spawn.type, this.groundLayer
      );
      this.enemyManager.addEnemy(enemy);

      // Assign patrol path from editor-placed waypoints (tile coords → world pixels)
      if (layout && spawn.waypoints && spawn.waypoints.length > 0) {
        const worldWaypoints = spawn.waypoints.map((t) => ({
          x: t.x * TILE_SIZE + TILE_SIZE / 2,
          y: t.y * TILE_SIZE + TILE_SIZE / 2,
        }));
        enemy.setPatrolPath(worldWaypoints, 'loop');
      }
      // Assign patrol path if defined in level metadata
      if (!layout && levelMeta?.patrolPaths && spawn.patrolPath) {
        const pathTiles: { x: number; y: number }[] = levelMeta.patrolPaths[spawn.patrolPath] ?? [];
        if (pathTiles.length > 0) {
          const worldWaypoints = pathTiles.map((t: { x: number; y: number }) => ({
            x: t.x * TILE_SIZE + TILE_SIZE / 2,
            y: t.y * TILE_SIZE + TILE_SIZE / 2,
          }));
          const loopMode = (spawn.patrolLoop as 'loop' | 'ping_pong' | 'once') ?? 'loop';
          enemy.setPatrolPath(worldWaypoints, loopMode);
        }
      }
    }

    // Spawn explosive barrels — from layout (editor test play) or level metadata
    const barrelList: { x: number; y: number }[] =
      layout?.barrels ?? (!GameScene.useGeneratedLevel ? (levelMeta?.barrels ?? []) : []);
    for (const b of barrelList) {
      this.spawnBarrel(b.x, b.y);
    }

    // Spawn destructible buildings — from layout (editor test play) or level metadata
    this.buildings = [];
    if (layout?.enemyBuildings && layout.enemyBuildings.length > 0) {
      for (const b of layout.enemyBuildings) {
        const worldX = b.x * TILE_SIZE + TILE_SIZE / 2;
        const worldY = b.y * TILE_SIZE + TILE_SIZE / 2;
        const building = new DestructibleBuilding(this, worldX, worldY, TILE_SIZE, TILE_SIZE, false);
        this.buildings.push(building);
      }
    } else if (!GameScene.useGeneratedLevel && levelMeta?.buildings) {
      for (const b of levelMeta.buildings as { x: number; y: number; width: number; height: number; isDestroyTarget?: boolean }[]) {
        const worldX = b.x * TILE_SIZE + (b.width * TILE_SIZE) / 2;
        const worldY = b.y * TILE_SIZE + (b.height * TILE_SIZE) / 2;
        const building = new DestructibleBuilding(
          this, worldX, worldY,
          b.width * TILE_SIZE,
          b.height * TILE_SIZE,
          b.isDestroyTarget ?? false
        );
        this.buildings.push(building);
      }
    }

    // Building destroyed event — check mission objective if it was a destroy target
    this.events.on('buildingDestroyed', (building: DestructibleBuilding) => {
      if (building.isDestroyTarget) {
        this.missionSystem.notifyDestroyTargetDestroyed();
      }
    });


    // Listen for rocket trooper explosions
    this.events.on('enemyRocketExplode', (x: number, y: number) => {
      this.onEnemyRocketExplode(x, y);
    });

    // Muzzle flash events from CombatSystem (both player + enemy bullets)
    this.events.on('muzzleFlash', (x: number, y: number) => {
      this.showMuzzleFlash(x, y);
      this.audioSystem.playGunshot();
    });

    // Blood splat on bullet impact
    this.events.on('bloodSplat', (x: number, y: number) => {
      this.explosionSystem.spawnBloodSplat(x, y);
    });

    // Soldier/enemy death audio
    this.events.on('soldierDied', () => {
      this.audioSystem.playSoldierDeath();
    });

    this.events.on('enemyDied', () => {
      this.audioSystem.playSoldierDeath();
      this.killCount++;
      this.registry.set('kills', this.killCount);
    });

    // Enemy rocket launch audio
    this.events.on('enemyRocketLaunch', () => {
      this.audioSystem.playRocketLaunch();
    });

    // ── Mission System ────────────────────────────────────────────────────
    this.missionSystem = new MissionSystem(this);

    if (layout && layout.objectives.length > 0) {
      // Use generated objectives
      const missionObjectives = layout.objectives.map((obj) => ({
        id: obj.type,
        type: obj.type,
        label: obj.type === 'kill_all' ? 'Eliminate all enemies' : 'Destroy enemy target',
      }));
      this.missionSystem.setObjectives(missionObjectives);
      if (layout.destroyTarget) {
        this.missionSystem.setDestroyTarget(
          layout.destroyTarget.x, layout.destroyTarget.y,
          this.groundLayer, TILE_SIZE
        );
      }
    } else if (levelMeta?.objectives) {
      // Use level metadata objectives
      this.missionSystem.setObjectives(
        levelMeta.objectives.map((o: { type: string; label: string }) => ({
          id: o.type, type: o.type, label: o.label,
        }))
      );
      const dt = levelMeta.destroyTarget;
      this.missionSystem.setDestroyTarget(
        dt?.x ?? DESTROY_TARGET_TILE_X, dt?.y ?? DESTROY_TARGET_TILE_Y,
        this.groundLayer, TILE_SIZE
      );
    } else {
      // Hardcoded fallback
      this.missionSystem.setObjectives([
        { id: 'kill_all', type: 'kill_all', label: 'Eliminate all enemies' },
        { id: 'destroy_hq', type: 'destroy_target_building', label: 'Destroy enemy HQ' },
      ]);
      this.missionSystem.setDestroyTarget(
        DESTROY_TARGET_TILE_X, DESTROY_TARGET_TILE_Y,
        this.groundLayer, TILE_SIZE
      );
    }

    this.missionSystem.on('mission_complete', () => {
      this.onMissionComplete();
    });

    // ── HUD ──────────────────────────────────────────────────────────────
    const { width } = this.scale;
    this.add
      .text(width / 2, 20, 'Warboys', {
        fontSize: '24px', color: '#ffffff',
        fontFamily: 'monospace', stroke: '#000000', strokeThickness: 4,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0).setScrollFactor(0).setDepth(100);

    this.grenadeHudText = this.add
      .text(12, 12, '', {
        fontSize: '14px', color: '#00ff88',
        fontFamily: 'monospace', stroke: '#000000', strokeThickness: 3,
        resolution: window.devicePixelRatio || 1,
      })
      .setScrollFactor(0).setDepth(101);

    this.rocketHudText = this.add
      .text(12, 30, '', {
        fontSize: '14px', color: '#ff8800',
        fontFamily: 'monospace', stroke: '#000000', strokeThickness: 3,
        resolution: window.devicePixelRatio || 1,
      })
      .setScrollFactor(0).setDepth(101);

    this.rocketModeIndicator = this.add
      .text(width / 2, 50, '🚀 ROCKET MODE', {
        fontSize: '16px', color: '#ff4400',
        fontFamily: 'monospace', stroke: '#000000', strokeThickness: 3,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0).setScrollFactor(0).setDepth(102).setVisible(false);

    this.targetingCursor = this.add.graphics().setDepth(90);

    // ── Keyboard ─────────────────────────────────────────────────────────
    if (this.input.keyboard) {
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.G)
        .on('down', () => this.handleGrenadeKey());
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.R)
        .on('down', () => this.handleRocketKey());
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.Q)
        .on('down', () => this.handleWeaponCycleKey());
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.TAB)
        .on('down', () => this.handleWeaponCycleKey());
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC)
        .on('down', () => this.cancelTargetingMode());
      // [F1] Toggle walkability debug overlay
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F1)
        .on('down', () => this.toggleWalkabilityOverlay(map));
      // [F2] Toggle collision geometry debug overlay
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F2)
        .on('down', () => this.toggleCollisionGeometryOverlay(map));
      // [D] Toggle enemy detection debug overlay
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D)
        .on('down', () => this.toggleDebugOverlay());
    }

    // Create the walkability debug overlay (hidden by default)
    this.walkabilityOverlay = this.add.graphics().setDepth(200).setVisible(false);
    // Create the enemy detection debug overlay (hidden by default)
    this.debugOverlay = this.add.graphics().setDepth(201).setVisible(false);
    // Create the collision geometry debug overlay (hidden by default)
    this.collisionGeometryOverlay = this.add.graphics().setDepth(202).setVisible(false);

    // Touch weapon commands emitted by HUD touch buttons
    this.events.off('touchCommand');
    this.events.on('touchCommand', (cmd: string) => {
      switch (cmd) {
        case 'grenade': this.handleGrenadeKey(); break;
        case 'rocket': this.handleRocketKey(); break;
        case 'cycle': this.handleWeaponCycleKey(); break;
        case 'cancel': this.cancelTargetingMode(); break;
      }
    });

    // ── Pointer ──────────────────────────────────────────────────────────
    // Custom cursor — not applicable on touch devices (iPadOS has no pointer cursor)
    if (!('ontouchstart' in window)) {
      this.input.setDefaultCursor(
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Ccircle cx='16' cy='16' r='6' stroke='%23ff2222' stroke-width='2' fill='none'/%3E%3Cline x1='16' y1='0' x2='16' y2='11' stroke='%23ff2222' stroke-width='2'/%3E%3Cline x1='16' y1='21' x2='16' y2='32' stroke='%23ff2222' stroke-width='2'/%3E%3Cline x1='0' y1='16' x2='11' y2='16' stroke='%23ff2222' stroke-width='2'/%3E%3Cline x1='21' y1='16' x2='32' y2='16' stroke='%23ff2222' stroke-width='2'/%3E%3C/svg%3E\") 16 16, crosshair"
      );
    }

    const isTouchDevice = navigator.maxTouchPoints > 0;

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Ensure AudioContext is unlocked on first user interaction
      this.audioSystem.resume();
      // Ignore taps in the on-screen touch button zone (bottom 100 px)
      if (isTouchDevice && pointer.y > this.scale.height - 100) return;
      if (pointer.leftButtonDown()) {
        // Double-tap shoot detection (touch only — desktop behaviour unchanged)
        if (isTouchDevice) {
          const now = this.time.now;
          const dist = Phaser.Math.Distance.Between(this.lastTapX, this.lastTapY, pointer.worldX, pointer.worldY);
          if (now - this.lastTapTime < 300 && dist > 10) {
            if (this.grenadeTargetingMode) {
              this.suppressNextLeftUp = true;
              this.throwGrenadeToPointer(pointer);
            } else if (this.rocketMode) {
              this.fireRocketToPointer(pointer);
            } else {
              this.handleShootClick(pointer);
            }
            this.lastTapTime = 0;
            return;
          }
          this.lastTapTime = now;
          this.lastTapX = pointer.worldX;
          this.lastTapY = pointer.worldY;
        }
        this.handleLeftDown(pointer);
      } else if (pointer.rightButtonDown()) {
        if (this.grenadeTargetingMode) {
          // Right-click also throws the grenade (same as left-click in targeting mode)
          this.suppressNextLeftUp = true;
          this.throwGrenadeToPointer(pointer);
        } else if (this.rocketMode) {
          this.fireRocketToPointer(pointer);
        } else {
          this.handleShootClick(pointer);
        }
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.grenadeTargetingMode) {
        const activeSoldier = this.getActiveSoldier();
        this.drawTargetingCursor(pointer.worldX, pointer.worldY, GRENADE_RADIUS, 0xff4400, activeSoldier.x, activeSoldier.y, GRENADE_MAX_THROW_RADIUS);
      }
      // Only pass drag events to squad manager for left-button drags
      if (pointer.leftButtonDown()) {
        this.squadManager.handlePointerMove(pointer);
      }
    });

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      // On touch, pointer.event is a TouchEvent — skip the right-button guard
      if (pointer.event instanceof MouseEvent && pointer.event.button !== 0) {
        return; // ignore right-click / middle-click releases
      }
      this.handleLeftUp(pointer);
    });

    this.game.canvas.addEventListener('contextmenu', this.onContextMenu);

    // Spawn ammo pickups — from layout (editor test play) or level metadata
    this.ammoPickups = [];
    const ammoList: { x: number; y: number; type: string; amount: number }[] =
      layout?.ammoPickups ?? (!GameScene.useGeneratedLevel ? (levelMeta?.ammoPickups ?? []) : []);
    for (const p of ammoList) {
      const worldX = p.x * TILE_SIZE + TILE_SIZE / 2;
      const worldY = p.y * TILE_SIZE + TILE_SIZE / 2;
      const pickup = new AmmoPickup(this, worldX, worldY, p.type as AmmoType, p.amount);
      this.ammoPickups.push(pickup);
    }

    // Launch HUD overlay scene
    this.scene.launch('HUD');

    // Initialise registry values for HUD
    this.registry.set('kills', 0);
    this.registry.set('grenades', 0);
    this.registry.set('rockets', 0);
    this.registry.set('currentWeapon', 'MACHINE GUN');
    this.registry.set('targetingMode', false);
    this.registry.set('worldWidth', this.mapWidth);
    this.registry.set('worldHeight', this.mapHeight);

    // Register shutdown cleanup via the scene event system so Phaser actually
    // calls it. Phaser does NOT call a scene's shutdown() method directly —
    // only the 'shutdown' event on this.events is fired by Systems.shutdown().
    this.events.once('shutdown', () => {
      // Destroy the Tilemap so its layers and frame references are released
      // before the next create() run. DisplayList.shutdown() destroys the
      // TilemapLayer GameObject, but the Tilemap data object is not a
      // GameObject and is not cleaned up automatically — leaving stale frame
      // references that cause Frame.data = null errors on restart.
      if (this.map) {
        this.map.destroy();
      }
      // Remove the contextmenu handler added directly to the canvas DOM element.
      // this.input listeners are cleaned up by Phaser; DOM listeners are not.
      this.game.canvas.removeEventListener('contextmenu', this.onContextMenu);
    });
  }

  // ── Left-click flow ──────────────────────────────────────────────────────

  private handleLeftDown(pointer: Phaser.Input.Pointer): void {
    if (this.grenadeTargetingMode) {
      this.suppressNextLeftUp = true;
      this.throwGrenadeToPointer(pointer);
      return;
    }
    this.squadManager.handlePointerDown(pointer);
  }

  private handleLeftUp(pointer: Phaser.Input.Pointer): void {
    if (this.grenadeTargetingMode) return;
    if (this.suppressNextLeftUp) {
      this.suppressNextLeftUp = false;
      return;
    }
    // Discard clicks that started before this scene was created — they leaked
    // from the previous scene (e.g. "START MISSION" button click-through).
    if (pointer.downTime < this.sceneStartTime) return;

    const isShift = !!(this.input.keyboard &&
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT).isDown);

    const consumed = this.squadManager.handlePointerUp(pointer, isShift);
    if (!consumed) {
      this.handleMoveClick(pointer);
    }
  }

  // ── Active soldier helper ────────────────────────────────────────────────

  private getActiveSoldier(): Soldier {
    const selected = this.squadManager.getSelected();
    return selected.length > 0 ? selected[0] : this.soldier;
  }

  // ── Grenade ──────────────────────────────────────────────────────────────

  private handleGrenadeKey(): void {
    const active = this.getActiveSoldier();
    if (active.isDead()) return;
    if (active.weapons.grenades <= 0) { return; }
    if (this.time.now < active.weapons.grenadeCooldownUntil) { return; }

    if (this.grenadeTargetingMode) {
      this.cancelTargetingMode();
    } else {
      this.rocketMode = false;
      this.rocketModeIndicator.setVisible(false);
      this.grenadeTargetingMode = true;
      // Switch weapon for ALL selected soldiers
      for (const s of this.squadManager.getSelected().filter(s => !s.isDead())) {
        s.selectWeapon('grenade');
      }
    }
  }

  private cancelTargetingMode(): void {
    this.grenadeTargetingMode = false;
    this.rocketMode = false;
    this.targetingCursor.clear();
    this.rocketModeIndicator.setVisible(false);
    // Reset weapon for ALL selected soldiers
    for (const s of this.squadManager.getSelected().filter(s => !s.isDead())) {
      s.selectWeapon('machinegun');
    }
  }

  private drawTargetingCursor(worldX: number, worldY: number, radius: number, color: number, rangeOriginX?: number, rangeOriginY?: number, maxRange?: number): void {
    this.targetingCursor.clear();
    this.targetingCursor.lineStyle(2, color, 0.9);
    this.targetingCursor.strokeCircle(worldX, worldY, radius);
    this.targetingCursor.lineStyle(1, color, 0.8);
    this.targetingCursor.lineBetween(worldX - 12, worldY, worldX + 12, worldY);
    this.targetingCursor.lineBetween(worldX, worldY - 12, worldX, worldY + 12);
    // Draw max throw range circle centred on the throwing soldier
    if (rangeOriginX !== undefined && rangeOriginY !== undefined && maxRange !== undefined) {
      this.targetingCursor.lineStyle(1, color, 0.35);
      this.targetingCursor.strokeCircle(rangeOriginX, rangeOriginY, maxRange);
    }
  }

  private throwGrenadeToPointer(pointer: Phaser.Input.Pointer): void {
    const active = this.getActiveSoldier();
    this.grenadeTargetingMode = false;
    this.targetingCursor.clear();
    active.weapons.grenades--;
    active.weapons.grenadeCooldownUntil = this.time.now + GRENADE_COOLDOWN;
    active.selectWeapon('machinegun');

    // Clamp target to max throw radius
    const dx = pointer.worldX - active.x;
    const dy = pointer.worldY - active.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const toX = dist <= GRENADE_MAX_THROW_RADIUS
      ? pointer.worldX
      : active.x + (dx / dist) * GRENADE_MAX_THROW_RADIUS;
    const toY = dist <= GRENADE_MAX_THROW_RADIUS
      ? pointer.worldY
      : active.y + (dy / dist) * GRENADE_MAX_THROW_RADIUS;

    this.grenades.push(new Grenade(
      this, active.x, active.y,
      toX, toY,
      (ex, ey) => this.onGrenadeExplode(ex, ey)
    ));
  }

  private onGrenadeExplode(x: number, y: number): void {
    this.audioSystem.playExplosion();
    this.explosionSystem.explode({
      x, y, radius: GRENADE_RADIUS, damage: GRENADE_DAMAGE,
      targets: this.buildExplosionTargets(),
    });
  }

  // ── Rocket launcher ──────────────────────────────────────────────────────

  private handleRocketKey(): void {
    const active = this.getActiveSoldier();
    if (active.isDead()) return;

    if (this.rocketMode) {
      // Toggle off
      this.rocketMode = false;
      this.rocketModeIndicator.setVisible(false);
      active.selectWeapon('machinegun');
    } else {
      if (active.weapons.rockets <= 0) { return; }
      this.rocketMode = true;
      this.rocketModeIndicator.setVisible(true);
      if (this.grenadeTargetingMode) {
        this.grenadeTargetingMode = false;
        this.targetingCursor.clear();
      }
      // Switch weapon for ALL selected soldiers
      for (const s of this.squadManager.getSelected().filter(s => !s.isDead())) {
        s.selectWeapon('rocket');
      }
    }
  }

  private handleWeaponCycleKey(): void {
    const active = this.getActiveSoldier();
    if (active.isDead()) return;

    const current = active.weapons.currentWeapon;
    // Cycle order: machinegun → grenade → rocket → machinegun, skipping empty weapons
    const order: Array<'machinegun' | 'grenade' | 'rocket'> = ['machinegun', 'grenade', 'rocket'];
    const currentIdx = order.indexOf(current);
    let nextIdx = (currentIdx + 1) % order.length;

    for (let i = 0; i < order.length - 1; i++) {
      const candidate = order[nextIdx];
      if (candidate === 'grenade' && active.weapons.grenades <= 0) {
        nextIdx = (nextIdx + 1) % order.length;
        continue;
      }
      if (candidate === 'rocket' && active.weapons.rockets <= 0) {
        nextIdx = (nextIdx + 1) % order.length;
        continue;
      }
      break;
    }

    const next = order[nextIdx];
    this.cancelTargetingMode();

    if (next === 'grenade') {
      this.handleGrenadeKey();
    } else if (next === 'rocket') {
      this.handleRocketKey();
    }
    // machinegun is the default after cancelTargetingMode
  }

  private fireRocketToPointer(pointer: Phaser.Input.Pointer): void {
    const active = this.getActiveSoldier();
    if (active.isDead()) return;
    if (active.weapons.rockets <= 0) {
      this.rocketMode = false;
      this.rocketModeIndicator.setVisible(false);
      active.selectWeapon('machinegun');
      return;
    }
    if (this.time.now < active.weapons.rocketCooldownUntil) { return; }

    active.weapons.rockets--;
    active.weapons.rocketCooldownUntil = this.time.now + ROCKET_COOLDOWN;
    this.rocketMode = false;
    this.rocketModeIndicator.setVisible(false);
    active.selectWeapon('machinegun');

    const angle = Phaser.Math.Angle.Between(active.x, active.y, pointer.worldX, pointer.worldY);
    active.setRotation(angle + Math.PI / 2);

    this.rockets.push(new Rocket(
      this, active.x, active.y,
      pointer.worldX, pointer.worldY,
      this.mapWidth, this.mapHeight,
      (ex, ey) => this.onPlayerRocketExplode(ex, ey)
    ));
    this.audioSystem.playRocketLaunch();
    this.showMuzzleFlash(active.x, active.y);
  }

  private onPlayerRocketExplode(x: number, y: number): void {
    this.audioSystem.playExplosion();
    this.explosionSystem.explode({
      x, y, radius: ROCKET_RADIUS, damage: ROCKET_DAMAGE,
      targets: this.buildExplosionTargets(),
      groundLayer: this.groundLayer, tileSize: TILE_SIZE,
    });
    // Check if the destroy objective target was hit
    this.missionSystem.checkDestroyTargetObjective();
  }

  private onMissionComplete(): void {
    if (this.transitioning) return;
    this.transitioning = true;
    // Advance campaign before transitioning and persist the new mission number.
    CampaignState.advanceMission();
    ProgressStore.save(CampaignState.missionNumber);
    this.cameras.main.fadeOut(800, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      const elapsed = this.time.now;
      const usedSpawns = (GameScene.useGeneratedLevel && GameScene.missionLayout)
        ? GameScene.missionLayout.playerSpawn.length
        : SQUAD_SPAWNS.length;
      const soldiersLost = usedSpawns - this.squadManager.getLiveSoldiers().length;
      this.scene.stop('HUD');
      const completedMission = CampaignState.missionNumber - 1;
      this.scene.start('MissionCompleteScene', {
        timeTaken: Math.floor(elapsed / 1000),
        troopsLost: soldiersLost,
        missionNumber: completedMission,
        levelId: `level${completedMission}`,
        kills: this.killCount,
      });
    });
  }

  private onEnemyRocketExplode(x: number, y: number): void {
    this.audioSystem.playExplosion();
    // Enemy rocket trooper fires weaker, smaller rockets
    this.explosionSystem.explode({
      x, y,
      radius: ROCKET_TROOPER_EXPLOSION_RADIUS,
      damage: ROCKET_TROOPER_EXPLOSION_DAMAGE,
      targets: this.squadManager.getLiveSoldiers(),
    });
  }

  private buildExplosionTargets() {
    const liveEnemies = this.enemyManager.getEnemies().filter((e) => !e.isDead());
    const liveSoldiers = this.squadManager.getLiveSoldiers();
    const liveBarrels = this.barrels.filter((b) => !b.isDead());
    const liveBuildings = this.buildings.filter((b) => !b.isDead());
    return [...liveEnemies, ...liveSoldiers, ...liveBarrels, ...liveBuildings];
  }

  private spawnBarrel(tileX: number, tileY: number): void {
    const worldX = tileX * TILE_SIZE + TILE_SIZE / 2;
    const worldY = tileY * TILE_SIZE + TILE_SIZE / 2;
    const barrel = new ExplosiveBarrel(this, worldX, worldY, (ex, ey) => {
      this.onBarrelDetonate(ex, ey);
    });
    this.barrels.push(barrel);
  }

  private onBarrelDetonate(x: number, y: number): void {
    this.audioSystem.playExplosion();
    this.explosionSystem.explode({
      x, y,
      radius: BARREL_SPLASH_RADIUS,
      damage: BARREL_SPLASH_DAMAGE,
      targets: this.buildExplosionTargets(),
      groundLayer: this.groundLayer,
      tileSize: TILE_SIZE,
    });
    this.missionSystem.checkDestroyTargetObjective();
  }

  // ── Standard input ───────────────────────────────────────────────────────

  private handleMoveClick(pointer: Phaser.Input.Pointer): void {
    const targetTile = this.groundLayer.getTileAtWorldXY(pointer.worldX, pointer.worldY);
    if (!targetTile) return;

    // Use walkability metadata from tileset.json when available
    const tilesetConfig = getTilesetConfig();
    const isBlocked = tilesetConfig
      ? !isTileWalkable(tilesetConfig, targetTile.index)
      : targetTile.index === 2 || targetTile.index === 4;
    if (isBlocked) return;

    this.showClickIndicator(pointer.worldX, pointer.worldY);

    for (const { soldier, tx, ty } of this.squadManager.getFormationTargets(
      pointer.worldX, pointer.worldY
    )) {
      this.movementSystem.findPath({ x: soldier.x, y: soldier.y }, { x: tx, y: ty }, (path) => {
        if (path && path.length > 1) soldier.move(path.slice(1), { x: tx, y: ty });
        else if (path && path.length === 1) soldier.move(path, { x: tx, y: ty });
      });
    }
  }

  private handleShootClick(pointer: Phaser.Input.Pointer): void {
    const selectedShooters = this.squadManager
      .getSelected()
      .filter((soldier) => !soldier.isDead());

    const firingSquad = selectedShooters.length > 0
      ? selectedShooters
      : this.squadManager.getLiveSoldiers();

    if (firingSquad.length === 0) {
      return;
    }

    for (const shooter of firingSquad) {
      const angle = Phaser.Math.Angle.Between(shooter.x, shooter.y, pointer.worldX, pointer.worldY);
      shooter.setRotation(angle + Math.PI / 2);
      this.combatSystem.fireBullet(shooter.x, shooter.y, pointer.worldX, pointer.worldY, false);
    }
  }

  private showMuzzleFlash(x: number, y: number): void {
    // In night mode the flash is larger and lasts slightly longer to cut through the darkness
    const radius = this.timeOfDay === 'night' ? 14 : 8;
    const duration = this.timeOfDay === 'night' ? 160 : 100;
    const flash = this.add.circle(x, y, radius, 0xffffff, 1).setDepth(40);
    this.tweens.add({
      targets: flash, alpha: 0, duration, ease: 'Power1',
      onComplete: () => flash.destroy(),
    });
  }

  private showClickIndicator(x: number, y: number): void {
    const circle = this.add.circle(x, y, 6, 0x00ff00, 0.8).setDepth(50);
    this.tweens.add({
      targets: circle, alpha: 0, scaleX: 2, scaleY: 2, duration: 500, ease: 'Power2',
      onComplete: () => circle.destroy(),
    });
  }

  // ── Map ──────────────────────────────────────────────────────────────────

  private buildGrid(map: Phaser.Tilemaps.Tilemap): number[][] {
    const tilesetConfig = getTilesetConfig();
    const grid: number[][] = [];
    for (let y = 0; y < map.height; y++) {
      const row: number[] = [];
      for (let x = 0; x < map.width; x++) {
        const tile = map.getTileAt(x, y, false, 'ground');
        let isBlocked: boolean;
        if (tile) {
          if (tilesetConfig) {
            // Use walkability metadata from tileset.json
            isBlocked = !isTileWalkable(tilesetConfig, tile.index);
          } else {
            // Fallback: hard-coded non-walkable indices
            isBlocked = tile.index === 2 || tile.index === 4;
          }
        } else {
          isBlocked = true; // treat missing tiles as blocked
        }
        row.push(isBlocked ? 1 : 0);
      }
      grid.push(row);
    }
    return grid;
  }

  // ── Update ───────────────────────────────────────────────────────────────

  update(time: number, delta: number): void {
    // ── Edge-scroll camera ────────────────────────────────────────────────
    const SCROLL_SPEED = 8;
    const SCROLL_ZONE = 40;
    const pointer = this.input.activePointer;
    const cam = this.cameras.main;
    if (pointer.x < SCROLL_ZONE) cam.scrollX -= SCROLL_SPEED;
    else if (pointer.x > this.scale.width - SCROLL_ZONE) cam.scrollX += SCROLL_SPEED;
    if (pointer.y < SCROLL_ZONE) cam.scrollY -= SCROLL_SPEED;
    else if (pointer.y > this.scale.height - SCROLL_ZONE) cam.scrollY += SCROLL_SPEED;

    const liveSoldiers = this.squadManager.getLiveSoldiers();

    // Update HUD registry
    this.registry.set('soldiers', liveSoldiers.map((s) => ({
      x: s.x,
      y: s.y,
      name: s.soldierName,
      hp: s.health,
      maxHp: 100,
      weapon: s.getWeaponLabel(),
      isSelected: s.isSelected,
    })));
    this.registry.set('enemies', this.enemyManager.getEnemies()
      .filter((e) => !e.isDead())
      .map((e) => ({ x: e.x, y: e.y })));

    // Bottom-centre weapon indicator shows active soldier's ammo
    const activeSoldier = this.getActiveSoldier();
    this.registry.set('grenades', activeSoldier.weapons.grenades);
    this.registry.set('rockets', activeSoldier.weapons.rockets);
    this.registry.set('currentWeapon',
      this.rocketMode ? 'ROCKETS' : (this.grenadeTargetingMode ? 'GRENADES' : 'MACHINE GUN'));
    this.registry.set('targetingMode', this.grenadeTargetingMode || this.rocketMode);

    // Update top-left HUD text with active soldier's ammo
    this.grenadeHudText.setText(`[G/Q] Grenades: ${activeSoldier.weapons.grenades}${this.grenadeTargetingMode ? ' ◀ ACTIVE' : ''}`);
    this.rocketHudText.setText(`[R/Q] Rockets: ${activeSoldier.weapons.rockets}${this.rocketMode ? ' ◀ ACTIVE' : ''}`);
    // Rotate idle selected soldiers to face the cursor
    for (const s of this.squadManager.getSelected()) {
      if (!s.isDead() && !s.isMoving) {
        const angle = Phaser.Math.Angle.Between(s.x, s.y, pointer.worldX, pointer.worldY);
        s.setRotation(angle + Math.PI / 2);
      }
    }
    for (const s of liveSoldiers) s.update(delta);

    // Ammo pickup collection
    if (this.ammoPickups.length > 0) {
      const soldiers = this.squadManager.getLiveSoldiers();
      for (const pickup of this.ammoPickups) {
        if (pickup.isCollected()) continue;
        for (const soldier of soldiers) {
          if (pickup.tryCollect(soldier.x, soldier.y)) {
            // Grant ammo to the soldier who picked it up
            if (pickup.ammoType === 'grenade') {
              soldier.weapons.grenades += pickup.amount;
            } else if (pickup.ammoType === 'rocket') {
              soldier.weapons.rockets += pickup.amount;
            }
            break;
          }
        }
      }
      // Remove collected pickups
      this.ammoPickups = this.ammoPickups.filter(p => !p.isCollected());
    }

    // Update EnemyManager (assigns/revokes chase slots)
    this.enemyManager.update();

    const liveEnemies = this.enemyManager.getEnemies().filter((e) => !e.isDead());
    const liveBuildings = this.buildings.filter(b => !b.isDead());
    const target = liveSoldiers[0];
    const playerBullets = this.combatSystem.getPlayerBullets();

    for (const enemy of liveEnemies) {
      enemy.update(
        delta,
        target ? target.x : 0,
        target ? target.y : 0,
        playerBullets
      );
    }

    // Collect all enemy rockets for wall-collision and proximity checks
    const tilesetConfig = getTilesetConfig();
    for (const enemy of liveEnemies) {
      for (const r of enemy.getRockets()) {
        if (!r.active) continue;
        // Wall collision
        const tile = this.groundLayer.getTileAtWorldXY(r.x, r.y);
        if (tile) {
          const hitWall = tilesetConfig
            ? !isTileWalkable(tilesetConfig, tile.index)
            : tile.index === 2 || tile.index === 4;
          if (hitWall) { r.triggerExplosion(); continue; }
        }
        // Proximity detonation against player soldiers
        for (const soldier of liveSoldiers) {
          if (soldier.isDead()) continue;
          const dist = Phaser.Math.Distance.Between(r.x, r.y, soldier.x, soldier.y);
          if (dist <= ROCKET_TRIGGER_RADIUS) { r.triggerExplosion(); break; }
        }
      }
    }

    // Player grenades
    for (let i = this.grenades.length - 1; i >= 0; i--) {
      const g = this.grenades[i];
      if (!g.active) { this.grenades.splice(i, 1); continue; }
      g.update(delta);
    }

    // Player rockets
    for (let i = this.rockets.length - 1; i >= 0; i--) {
      const r = this.rockets[i];
      if (!r.active) { this.rockets.splice(i, 1); continue; }
      // Wall collision
      const tile = this.groundLayer.getTileAtWorldXY(r.x, r.y);
      if (tile) {
        const hitWall = tilesetConfig
          ? !isTileWalkable(tilesetConfig, tile.index)
          : tile.index === 2 || tile.index === 4;
        if (hitWall) {
          r.triggerExplosion();
          this.rockets.splice(i, 1);
          continue;
        }
      }
      // Proximity detonation against live enemies and buildings
      let detonated = false;
      const rocketTargets = [...liveEnemies, ...liveBuildings];
      for (const target of rocketTargets) {
        if (target.isDead()) continue;
        const dist = Phaser.Math.Distance.Between(r.x, r.y, target.x, target.y);
        if (dist <= ROCKET_TRIGGER_RADIUS) {
          r.triggerExplosion();
          this.rockets.splice(i, 1);
          detonated = true;
          break;
        }
      }
      if (!detonated) r.update(delta);
    }

    // Remove detonated barrels
    this.barrels = this.barrels.filter(b => !b.isDead());

    this.combatSystem.update(delta);
    this.combatSystem.checkPlayerBulletsVsTargets(liveEnemies);
    const liveBarrels = this.barrels.filter(b => !b.isDead());
    if (liveBarrels.length > 0) {
      this.combatSystem.checkPlayerBulletsVsTargets(liveBarrels);
    }
    if (liveBuildings.length > 0) {
      this.combatSystem.checkPlayerBulletsVsTargets(liveBuildings);
    }
    if (FRIENDLY_FIRE && liveSoldiers.length > 0) {
      this.combatSystem.checkPlayerBulletsVsTargets(liveSoldiers);
    }
    if (liveSoldiers.length > 0) {
      this.combatSystem.checkEnemyBulletsVsTargets(liveSoldiers);
    }

    // Mission system checks
    if (liveEnemies.length === 0 && this.enemyManager.getEnemies().length > 0) {
      this.missionSystem.notifyAllEnemiesDead();
    }
    this.missionSystem.update(time, delta);

    // Detect all soldiers dead → mission fail
    if (liveSoldiers.length === 0 && this.squadManager.getSoldiers().length > 0) {
      this.onMissionFail();
    }

    // Refresh enemy detection debug overlay each frame when visible
    if (this.debugOverlayVisible) {
      this.updateDebugOverlay();
    }
  }

  private onMissionFail(): void {
    if (this.transitioning) return;
    this.transitioning = true;
    this.cameras.main.fadeOut(800, 80, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.stop('HUD');
      this.scene.start('MissionFailScene', { reason: 'All soldiers have fallen.' });
    });
  }

  // ── Enemy detection debug overlay ────────────────────────────────────────

  /**
   * Toggle the enemy detection debug overlay on/off.
   * Yellow circle = sight radius, Blue circle = hearing radius.
   * Press D in-game to toggle.
   */
  private toggleDebugOverlay(): void {
    this.debugOverlayVisible = !this.debugOverlayVisible;
    this.debugOverlay.setVisible(this.debugOverlayVisible);
  }

  private updateDebugOverlay(): void {
    this.debugOverlay.clear();
    for (const enemy of this.enemyManager.getEnemies()) {
      if (enemy.isDead()) continue;
      // Sight radius — yellow
      this.debugOverlay.lineStyle(1, 0xffff00, 0.5);
      this.debugOverlay.strokeCircle(enemy.x, enemy.y, ENEMY_SIGHT_RADIUS);
      // Hearing radius — blue
      this.debugOverlay.lineStyle(1, 0x4488ff, 0.4);
      this.debugOverlay.strokeCircle(enemy.x, enemy.y, ENEMY_HEARING_RADIUS);
    }
  }

  // ── Walkability debug overlay ─────────────────────────────────────────────

  /**
   * Toggle the walkability overlay on/off.
   * Green = walkable, Red = non-walkable.
   * Press F1 in-game to toggle.
   */
  private toggleWalkabilityOverlay(map: Phaser.Tilemaps.Tilemap): void {
    this.walkabilityOverlayVisible = !this.walkabilityOverlayVisible;
    this.walkabilityOverlay.setVisible(this.walkabilityOverlayVisible);

    if (this.walkabilityOverlayVisible) {
      this.drawWalkabilityOverlay(map);
    }
  }

  private drawWalkabilityOverlay(map: Phaser.Tilemaps.Tilemap): void {
    this.walkabilityOverlay.clear();
    const tilesetConfig = getTilesetConfig();

    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const tile = map.getTileAt(x, y, false, 'ground');
        if (!tile) continue;

        let walkable: boolean;
        if (tilesetConfig) {
          walkable = isTileWalkable(tilesetConfig, tile.index);
        } else {
          walkable = !(tile.index === 2 || tile.index === 4);
        }

        const px = x * TILE_SIZE;
        const py = y * TILE_SIZE;

        if (walkable) {
          this.walkabilityOverlay.fillStyle(0x00ff00, 0.25);
        } else {
          this.walkabilityOverlay.fillStyle(0xff0000, 0.35);
        }
        this.walkabilityOverlay.fillRect(px, py, TILE_SIZE, TILE_SIZE);

        // Draw a small indicator dot
        this.walkabilityOverlay.fillStyle(walkable ? 0x00ff88 : 0xff4444, 0.8);
        this.walkabilityOverlay.fillCircle(
          px + TILE_SIZE / 2,
          py + TILE_SIZE / 2,
          3
        );
      }
    }

    // Legend (fixed position — drawn in world space near origin, scrolls with camera)
    const lx = 4, ly = 4;
    this.walkabilityOverlay.fillStyle(0x000000, 0.6);
    this.walkabilityOverlay.fillRect(lx, ly, 140, 44);
    this.walkabilityOverlay.fillStyle(0x00ff00, 0.7);
    this.walkabilityOverlay.fillRect(lx + 4, ly + 6, 14, 14);
    this.walkabilityOverlay.fillStyle(0xff4444, 0.7);
    this.walkabilityOverlay.fillRect(lx + 4, ly + 24, 14, 14);
  }

  // ── Collision geometry debug overlay ─────────────────────────────────────

  /**
   * Toggle the collision geometry overlay on/off.
   * Orange lines = solid polygon outlines (walls).
   * Cyan dot = walkable area boundary corner.
   * Press F2 in-game to toggle.
   */
  private toggleCollisionGeometryOverlay(map: Phaser.Tilemaps.Tilemap): void {
    this.collisionGeometryOverlayVisible = !this.collisionGeometryOverlayVisible;
    this.collisionGeometryOverlay.setVisible(this.collisionGeometryOverlayVisible);

    if (this.collisionGeometryOverlayVisible) {
      this.drawCollisionGeometryOverlay(map);
    } else {
      this.collisionGeometryLabel?.destroy();
      this.collisionGeometryLabel = null;
    }
  }

  private drawCollisionGeometryOverlay(map: Phaser.Tilemaps.Tilemap): void {
    this.collisionGeometryOverlay.clear();

    // Build walkability grid from the current map (same logic as buildGrid)
    const walkabilityGrid = this.buildGrid(map);

    // Build tile index grid for the per-pixel mask compositing path
    const tileGrid: number[][] = [];
    for (let y = 0; y < map.height; y++) {
      const row: number[] = [];
      for (let x = 0; x < map.width; x++) {
        const tile = map.getTileAt(x, y, false, 'ground');
        row.push(tile ? tile.index : 0);
      }
      tileGrid.push(row);
    }

    const tilesetConfig = getTilesetConfig();
    const builder = new CollisionGeometryBuilder({ tileSize: TILE_SIZE });
    const geom = builder.build(tileGrid, walkabilityGrid, tilesetConfig);

    // Scale factor: tile-scale bitmap coords → world pixels
    const scale = tilesetConfig ? 1 : TILE_SIZE;

    // Draw solid polygon outlines in orange
    this.collisionGeometryOverlay.lineStyle(1.5, 0xff8800, 0.85);
    for (const poly of geom.solidPolygons) {
      if (poly.length < 2) continue;
      this.collisionGeometryOverlay.strokePoints(
        poly.map((p) => ({ x: p.x * scale, y: p.y * scale })),
        true  // closeShape — connect last point back to first
      );
    }

    // Draw walkable area boundary (map rect) in cyan
    const boundary = geom.walkableArea[0];
    if (boundary) {
      this.collisionGeometryOverlay.lineStyle(1, 0x00ffff, 0.4);
      this.collisionGeometryOverlay.strokePoints(
        boundary.map((p) => ({ x: p.x * scale, y: p.y * scale })),
        true
      );
    }

    // Stats legend — destroy any previous instance before recreating
    this.collisionGeometryLabel?.destroy();
    const labelText = `F2 Collision Geometry\nPolygons: ${geom.stats.simplifiedPolygonCount}  Verts: ${geom.stats.totalVertices}\nBuild: ${geom.stats.buildTimeMs.toFixed(1)} ms`;
    this.collisionGeometryLabel = this.add.text(4, 54, labelText, {
      fontSize: '10px',
      color: '#ff8800',
      backgroundColor: '#00000099',
      padding: { x: 4, y: 3 },
    }).setDepth(203).setScrollFactor(0);
  }
}
